import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth
} from 'date-fns';
import { useApi } from '../hooks/useApi';
import { useUser } from '../hooks/useUser';
import { sharedStyles } from '../styles/sharedStyles';
import {
  isMachineSchedulingUnavailable,
  machineSchedulingOptionSuffix,
  machineSchedulingUnavailableNote
} from '../utils/machineScheduling';

const weekdayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Matches server default palette in `scheduleController` */
const SCHEDULE_COLOR_PRESETS = [
  '#5B8DEF',
  '#E27D60',
  '#6ECB98',
  '#C18AE8',
  '#F2C94C',
  '#56B4E9',
  '#E57373',
  '#7C6FAD',
  '#2BB0A3'
];

function isHexColor6(s) {
  return typeof s === 'string' && /^#[0-9A-Fa-f]{6}$/.test(s.trim());
}

function entryAccentColorForPrint(en) {
  return isHexColor6(en?.color) ? en.color.trim() : '#5B8DEF';
}

function entryCoversDay(entry, day) {
  const dStr = format(day, 'yyyy-MM-dd');
  return entry.startDate <= dStr && entry.endDate >= dStr;
}

/** Client-side text filter for calendar chips (API already applies dropdown filters). */
function entryMatchesScheduleSearch(en, qLower) {
  const haystacks = [
    en.taskDescription,
    en.notes,
    en.status,
    String(en.id),
    en.Customer?.companyName,
    en.Customer?.contactPerson,
    en.Customer?.email,
    en.Job?.title,
    en.Job?.location,
    en.Ticket?.ticketNumber != null ? String(en.Ticket.ticketNumber) : '',
    en.AssignedUser?.name,
    en.AssignedUser?.email,
    ...(en.Machines || []).flatMap((m) => [m.name, m.model])
  ];
  return haystacks.some((s) => s != null && String(s).toLowerCase().includes(qLower));
}

const SCHEDULE_DRAG_MIME = 'application/x-griffin-schedule-entry';

/** Injected while this page is mounted so browser print uses landscape (full calendar width). */
const SCHEDULE_PRINT_PAGE_STYLE_ID = 'schedule-route-print-page';

function parseScheduleDragPayload(raw, fallbackFromDate) {
  if (raw == null || raw === '') return null;
  try {
    const o = JSON.parse(raw);
    const id = parseInt(o.id, 10);
    const fromDate = String(o.fromDate || '').slice(0, 10);
    if (id && fromDate) return { id, fromDate };
  } catch {
    /* legacy: plain id string */
  }
  const id = parseInt(raw, 10);
  if (id && fallbackFromDate) return { id, fromDate: String(fallbackFromDate).slice(0, 10) };
  return null;
}

function ymdIsSunday(ymd) {
  const part = String(ymd || '').slice(0, 10);
  const [y, m, d] = part.split('-').map((n) => parseInt(n, 10));
  if (!y || !m || !d) return true;
  return new Date(y, m - 1, d).getDay() === 0;
}

function alphaCompare(a, b) {
  return String(a || '').localeCompare(String(b || ''), undefined, {
    sensitivity: 'base',
    numeric: true
  });
}

function assignableUserLabel(u) {
  return (u?.email || u?.name || '').trim() || `User #${u?.id}`;
}

const EMAIL_ADDR_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

function parseEmailDraft(text) {
  return String(text || '')
    .split(/[,;\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Chip-style multi-email field (Enter / comma / paste to add).
 */
function EmailChipField({ id, label, emails, onChange, placeholder, hint, excludedEmails = [] }) {
  const [draft, setDraft] = useState('');
  const [fieldError, setFieldError] = useState('');
  const excluded = useMemo(
    () => new Set(excludedEmails.map((e) => String(e).toLowerCase())),
    [excludedEmails]
  );

  const commitDraft = (raw = draft) => {
    const parts = parseEmailDraft(raw);
    if (!parts.length) {
      setDraft('');
      setFieldError('');
      return;
    }
    const next = [...emails];
    const seen = new Set(next.map((e) => e.toLowerCase()));
    const bad = [];
    const dupes = [];
    for (const part of parts) {
      if (!EMAIL_ADDR_RE.test(part)) {
        bad.push(part);
        continue;
      }
      const key = part.toLowerCase();
      if (seen.has(key) || excluded.has(key)) {
        dupes.push(part);
        continue;
      }
      seen.add(key);
      next.push(part);
    }
    if (bad.length) {
      setFieldError(`Not a valid email: ${bad.join(', ')}`);
      setDraft(bad.join(', '));
    } else {
      setFieldError(dupes.length ? `Already added: ${dupes.join(', ')}` : '');
      setDraft('');
    }
    if (next.length !== emails.length) onChange(next);
  };

  const removeAt = (idx) => {
    onChange(emails.filter((_, i) => i !== idx));
  };

  return (
    <div className="mb-3">
      {label ? (
        <label className="form-label" htmlFor={id}>
          {label}
        </label>
      ) : null}
      <div
        className="form-control d-flex flex-wrap align-items-center gap-1"
        style={{ minHeight: '2.5rem', height: 'auto', cursor: 'text' }}
        onClick={(e) => {
          const input = e.currentTarget.querySelector('input');
          if (input) input.focus();
        }}
      >
        {emails.map((email, idx) => (
          <span
            key={`${email}-${idx}`}
            className="badge rounded-pill d-inline-flex align-items-center gap-1"
            style={{
              backgroundColor: '#e8eef8',
              color: '#1a1f36',
              fontWeight: 500,
              fontSize: '0.8125rem',
              padding: '0.35em 0.55em 0.35em 0.75em'
            }}
          >
            {email}
            <button
              type="button"
              className="btn-close"
              style={{ fontSize: '0.55rem', opacity: 0.65 }}
              aria-label={`Remove ${email}`}
              onClick={(e) => {
                e.stopPropagation();
                removeAt(idx);
              }}
            />
          </span>
        ))}
        <input
          id={id}
          type="text"
          className="border-0 flex-grow-1"
          style={{ outline: 'none', minWidth: '10rem', background: 'transparent', padding: '0.15rem 0' }}
          value={draft}
          placeholder={emails.length ? 'Add another…' : placeholder}
          autoComplete="email"
          onChange={(e) => {
            setFieldError('');
            const v = e.target.value;
            if (/[,;]/.test(v)) {
              commitDraft(v);
            } else {
              setDraft(v);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === 'Tab') {
              if (draft.trim()) {
                e.preventDefault();
                commitDraft();
              }
            } else if (e.key === 'Backspace' && !draft && emails.length) {
              onChange(emails.slice(0, -1));
            }
          }}
          onBlur={() => commitDraft()}
          onPaste={(e) => {
            const text = e.clipboardData?.getData('text');
            if (text && /[,;\s]/.test(text)) {
              e.preventDefault();
              commitDraft(`${draft} ${text}`);
            }
          }}
        />
      </div>
      {fieldError ? <div className="form-text text-danger">{fieldError}</div> : null}
      {hint && !fieldError ? <div className="form-text">{hint}</div> : null}
    </div>
  );
}

const Schedule = () => {
  const api = useApi();
  const { user, loading: userLoading } = useUser();
  const isAdmin = user?.role === 'admin';
  const canView = user?.role === 'admin' || user?.role === 'foreman';

  const [cursor, setCursor] = useState(() => new Date());
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [machines, setMachines] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [jobTickets, setJobTickets] = useState([]);
  const [jobTicketsLoading, setJobTicketsLoading] = useState(false);

  const [filterCustomerId, setFilterCustomerId] = useState('');
  const [filterJobId, setFilterJobId] = useState('');
  const [filterMachineId, setFilterMachineId] = useState('');
  const [filterForemanId, setFilterForemanId] = useState('');
  const [filterSearch, setFilterSearch] = useState('');

  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailToList, setEmailToList] = useState([]);
  const [emailCcList, setEmailCcList] = useState([]);
  const [emailBccList, setEmailBccList] = useState([]);
  const [emailShowCc, setEmailShowCc] = useState(false);
  const [emailShowBcc, setEmailShowBcc] = useState(false);
  const [emailNote, setEmailNote] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [conflictDialog, setConflictDialog] = useState(null);
  const [machineSearch, setMachineSearch] = useState('');
  const [machinePickerOpen, setMachinePickerOpen] = useState(false);
  const machinePickerRef = useRef(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [lockLoading, setLockLoading] = useState(false);
  const [unlockLoading, setUnlockLoading] = useState(false);
  /** Only the chip you drag from should dim — same entry repeats on each day it spans. */
  const [draggingEntryId, setDraggingEntryId] = useState(null);
  const [dragSourceCellKey, setDragSourceCellKey] = useState(null);
  const [dragOverDayKey, setDragOverDayKey] = useState(null);
  const [breakdownModalOpen, setBreakdownModalOpen] = useState(false);
  const [breakdownLoadingId, setBreakdownLoadingId] = useState(null);
  const [form, setForm] = useState({
    customerId: '',
    jobId: '',
    ticketId: '',
    taskDescription: '',
    machineIds: [],
    startDate: format(new Date(), 'yyyy-MM-dd'),
    workingDays: '5',
    useWorkingDays: true,
    endDate: '',
    bufferDays: '0',
    assignedUserId: '',
    notes: '',
    status: 'Draft',
    color: '',
    notificationsEnabled: true
  });

  const monthStart = startOfMonth(cursor);
  const monthEnd = endOfMonth(cursor);
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 0 });
  const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
  const days = eachDayOfInterval({ start: gridStart, end: gridEnd });

  const rangeFrom = format(gridStart, 'yyyy-MM-dd');
  const rangeTo = format(gridEnd, 'yyyy-MM-dd');

  const loadEntries = useCallback(async (opts = {}) => {
    const silent = opts.silent === true;
    if (!canView) return;
    if (!silent) {
      setLoading(true);
      setError('');
    }
    try {
      const params = new URLSearchParams({ from: rangeFrom, to: rangeTo });
      if (filterCustomerId) params.set('customerId', filterCustomerId);
      if (filterJobId) params.set('jobId', filterJobId);
      if (filterMachineId) params.set('machineId', filterMachineId);
      if (filterForemanId && isAdmin) params.set('foremanUserId', filterForemanId);
      const entRes = await api({ method: 'get', url: `/schedule?${params.toString()}` });
      if (entRes.data.success) setEntries(entRes.data.data || []);
    } catch (e) {
      if (!silent) {
        setError(e.response?.data?.message || 'Failed to load calendar');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [
    api,
    canView,
    rangeFrom,
    rangeTo,
    filterCustomerId,
    filterJobId,
    filterMachineId,
    filterForemanId,
    isAdmin
  ]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  const visibleEntries = useMemo(() => {
    const q = filterSearch.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter((en) => entryMatchesScheduleSearch(en, q));
  }, [entries, filterSearch]);

  /** Same ordering as customer email table (start date, then id). */
  const printTableEntries = useMemo(() => {
    return [...visibleEntries].sort((a, b) => {
      const sa = String(a.startDate || '');
      const sb = String(b.startDate || '');
      if (sa !== sb) return sa.localeCompare(sb);
      return (a.id || 0) - (b.id || 0);
    });
  }, [visibleEntries]);

  const selectedFilterCustomer = useMemo(() => {
    const id = parseInt(filterCustomerId, 10);
    if (!id) return null;
    return customers.find((c) => c.id === id) || null;
  }, [customers, filterCustomerId]);

  const printHeaderJobTitle = useMemo(() => {
    if (!filterJobId) return null;
    const jid = parseInt(filterJobId, 10);
    const j = jobs.find((x) => x.id === jid);
    return j?.title || null;
  }, [filterJobId, jobs]);

  const emailRecipientLooksValid = useMemo(() => {
    return emailToList.length > 0 && emailToList.every((e) => EMAIL_ADDR_RE.test(e));
  }, [emailToList]);

  const emailCcExcluded = useMemo(
    () => [...emailToList, ...emailBccList],
    [emailToList, emailBccList]
  );
  const emailBccExcluded = useMemo(
    () => [...emailToList, ...emailCcList],
    [emailToList, emailCcList]
  );
  const emailToExcluded = useMemo(
    () => [...emailCcList, ...emailBccList],
    [emailCcList, emailBccList]
  );

  useEffect(() => {
    setEmailSuccess('');
  }, [cursor, filterCustomerId, filterJobId, filterMachineId, filterForemanId]);

  useEffect(() => {
    if (!canView) {
      document.getElementById(SCHEDULE_PRINT_PAGE_STYLE_ID)?.remove();
      return undefined;
    }
    let style = document.getElementById(SCHEDULE_PRINT_PAGE_STYLE_ID);
    if (!style) {
      style = document.createElement('style');
      style.id = SCHEDULE_PRINT_PAGE_STYLE_ID;
      document.head.appendChild(style);
    }
    style.textContent = `@media print {
  @page {
    size: A4 landscape;
    margin: 5mm 6mm;
  }
}`;
    return () => {
      document.getElementById(SCHEDULE_PRINT_PAGE_STYLE_ID)?.remove();
    };
  }, [canView]);

  useEffect(() => {
    if (!canView) return;
    const loadMeta = async () => {
      try {
        const [m, c, j] = await Promise.all([
          api({ method: 'get', url: '/machines?limit=2000' }),
          api({ method: 'get', url: '/customers?limit=2000' }),
          api({ method: 'get', url: '/jobs?limit=2000' })
        ]);
        if (m.data.success) {
          setMachines(
            [...(m.data.data?.machines || [])].sort((a, b) => alphaCompare(a.name, b.name))
          );
        }
        if (c.data.success) {
          setCustomers(
            [...(c.data.data?.customers || [])].sort((a, b) =>
              alphaCompare(a.companyName, b.companyName)
            )
          );
        }
        if (j.data.success) {
          setJobs([...(j.data.data?.jobs || [])].sort((a, b) => alphaCompare(a.title, b.title)));
        }
        if (isAdmin) {
          const u = await api({ method: 'get', url: '/users' });
          if (u.data.success) {
            setAssignableUsers(
              [...(u.data.data || [])].sort((a, b) =>
                alphaCompare(assignableUserLabel(a), assignableUserLabel(b))
              )
            );
          }
        }
      } catch (_) {
        /* non-fatal */
      }
    };
    loadMeta();
  }, [api, canView, isAdmin]);

  const jobsForCustomer = useMemo(() => {
    const cid = parseInt(form.customerId, 10);
    if (!cid) return [];
    return jobs
      .filter((j) => j.customerId === cid)
      .slice()
      .sort((a, b) => alphaCompare(a.title, b.title));
  }, [jobs, form.customerId]);

  const jobsForFilter = useMemo(() => {
    const cid = parseInt(filterCustomerId, 10);
    const list = cid ? jobs.filter((j) => j.customerId === cid) : jobs;
    return list.slice().sort((a, b) => alphaCompare(a.title, b.title));
  }, [jobs, filterCustomerId]);

  const assignableUsersForSelect = useMemo(() => {
    return assignableUsers
      .filter((u) => u.role === 'foreman' || u.role === 'operator')
      .slice()
      .sort((a, b) => alphaCompare(assignableUserLabel(a), assignableUserLabel(b)));
  }, [assignableUsers]);

  useEffect(() => {
    if (!modalOpen) {
      setJobTickets([]);
      return;
    }
    const cid = parseInt(form.customerId, 10);
    const jid = parseInt(form.jobId, 10);
    if (!cid || !jid) {
      setJobTickets([]);
      return;
    }
    let cancelled = false;
    setJobTicketsLoading(true);
    (async () => {
      try {
        const res = await api({
          method: 'get',
          url: '/tickets',
          params: { customerId: cid, jobId: jid, limit: 500, page: 1 }
        });
        if (!cancelled && res.data?.success) {
          setJobTickets(res.data.data?.tickets || []);
        }
      } catch {
        if (!cancelled) setJobTickets([]);
      } finally {
        if (!cancelled) setJobTicketsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [modalOpen, form.customerId, form.jobId, api]);

  const ticketsForScheduleSelect = useMemo(() => {
    const list = [...jobTickets];
    const selId = parseInt(form.ticketId, 10);
    if (selId && !list.some((t) => t.id === selId)) {
      const t = editing?.Ticket;
      list.unshift({
        id: selId,
        ticketNumber: t?.ticketNumber != null ? t.ticketNumber : String(selId),
        workDate: t?.workStartDate || editing?.workDate || '',
        description: ''
      });
    }
    return list.sort((a, b) => b.id - a.id);
  }, [jobTickets, form.ticketId, editing]);

  const machineOptions = useMemo(() => {
    return machines.map((m) => ({
      ...m,
      schedulingHint: machineSchedulingUnavailableNote(m, form.startDate)
    }));
  }, [machines, form.startDate]);

  const sortedMachinePickerList = useMemo(() => {
    return [...machineOptions].sort((a, b) =>
      String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' })
    );
  }, [machineOptions]);

  const filteredMachinePickerList = useMemo(() => {
    const q = machineSearch.trim().toLowerCase();
    if (!q) return sortedMachinePickerList;
    return sortedMachinePickerList.filter(
      (m) =>
        String(m.name || '')
          .toLowerCase()
          .includes(q) ||
        String(m.model || '')
          .toLowerCase()
          .includes(q)
    );
  }, [sortedMachinePickerList, machineSearch]);

  const selectedMachinesOrdered = useMemo(() => {
    const byId = new Map(machineOptions.map((m) => [m.id, m]));
    return form.machineIds.map((id) => byId.get(id)).filter(Boolean);
  }, [form.machineIds, machineOptions]);

  useEffect(() => {
    if (!modalOpen) {
      setMachineSearch('');
      setMachinePickerOpen(false);
      setSaveLoading(false);
      setLockLoading(false);
      setUnlockLoading(false);
      setBreakdownModalOpen(false);
      setBreakdownLoadingId(null);
    }
  }, [modalOpen]);

  const scheduleModalBusy = saveLoading || lockLoading || unlockLoading;

  useEffect(() => {
    if (!machinePickerOpen) return;
    const onPointerDown = (e) => {
      if (machinePickerRef.current && !machinePickerRef.current.contains(e.target)) {
        setMachinePickerOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setMachinePickerOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [machinePickerOpen]);

  const openNew = () => {
    setEditing(null);
    setForm({
      customerId: filterCustomerId || '',
      jobId: filterJobId || '',
      ticketId: '',
      taskDescription: '',
      machineIds: [],
      startDate: format(new Date(), 'yyyy-MM-dd'),
      workingDays: '5',
      useWorkingDays: true,
      endDate: '',
      bufferDays: '0',
      assignedUserId: '',
      notes: '',
      status: 'Draft',
      color: '',
      notificationsEnabled: true
    });
    setModalOpen(true);
  };

  const openEdit = (entry) => {
    setEditing(entry);
    const hasWd = entry.workingDays != null && entry.workingDays !== '';
    setForm({
      customerId: String(entry.customerId),
      jobId: String(entry.jobId),
      ticketId: entry.ticketId ? String(entry.ticketId) : '',
      taskDescription: entry.taskDescription || '',
      machineIds: (entry.Machines || []).map((x) => x.id),
      startDate: entry.startDate,
      workingDays: hasWd ? String(entry.workingDays) : '5',
      useWorkingDays: hasWd,
      endDate: entry.endDate,
      bufferDays: String(entry.bufferDays ?? 0),
      assignedUserId: entry.assignedUserId ? String(entry.assignedUserId) : '',
      notes: entry.notes || '',
      status: entry.status,
      color: entry.color || '',
      notificationsEnabled: entry.notificationsEnabled !== false
    });
    setModalOpen(true);
  };

  const machinesFormDisabled = !isAdmin || (editing && editing.status === 'Locked');

  const toggleMachineId = (id) => {
    if (machinesFormDisabled) return;
    setForm((f) => {
      const has = f.machineIds.includes(id);
      return {
        ...f,
        machineIds: has ? f.machineIds.filter((x) => x !== id) : [...f.machineIds, id]
      };
    });
  };

  const removeMachineId = (id) => {
    if (machinesFormDisabled) return;
    setForm((f) => ({ ...f, machineIds: f.machineIds.filter((x) => x !== id) }));
  };

  const recomputeEnd = async () => {
    if (!form.useWorkingDays) return;
    try {
      const res = await api({
        method: 'post',
        url: '/schedule/compute-end',
        data: {
          startDate: form.startDate,
          workingDays: parseInt(form.workingDays, 10) || 1,
          bufferDays: parseInt(form.bufferDays, 10) || 0
        }
      });
      if (res.data.success) {
        setForm((f) => ({ ...f, endDate: res.data.data.endDate }));
      }
    } catch (e) {
      setError(e.response?.data?.message || 'Could not compute end date');
    }
  };

  useEffect(() => {
    if (!modalOpen || !form.useWorkingDays) return;
    const t = setTimeout(() => recomputeEnd(), 300);
    return () => clearTimeout(t);
  }, [modalOpen, form.useWorkingDays, form.startDate, form.workingDays, form.bufferDays]);

  const saveEntry = async (acknowledgeConflicts) => {
    setError('');
    setSaveLoading(true);
    const payload = {
      customerId: parseInt(form.customerId, 10),
      jobId: parseInt(form.jobId, 10),
      ticketId: form.ticketId ? parseInt(form.ticketId, 10) : null,
      taskDescription: form.taskDescription,
      machineIds: form.machineIds.map((id) => parseInt(id, 10)),
      startDate: form.startDate,
      bufferDays: parseInt(form.bufferDays, 10) || 0,
      assignedUserId: form.assignedUserId ? parseInt(form.assignedUserId, 10) : null,
      notes: form.notes || null,
      status: form.status,
      color: form.color || undefined,
      notificationsEnabled: form.notificationsEnabled,
      acknowledgeConflicts: !!acknowledgeConflicts
    };
    if (form.useWorkingDays) {
      payload.workingDays = parseInt(form.workingDays, 10) || 1;
    } else {
      payload.endDate = form.endDate;
      payload.workingDays = null;
    }

    try {
      if (editing) {
        await api({ method: 'put', url: `/schedule/${editing.id}`, data: payload });
      } else {
        await api({ method: 'post', url: '/schedule', data: payload });
      }
      setModalOpen(false);
      setConflictDialog(null);
      loadEntries();
    } catch (e) {
      if (e.response?.status === 409 && e.response?.data?.code === 'SCHEDULE_CONFLICT') {
        setConflictDialog({
          message: e.response.data.message,
          conflicts: e.response.data.conflicts || [],
          code: e.response.data.code
        });
        return;
      }
      setError(e.response?.data?.message || 'Save failed');
    } finally {
      setSaveLoading(false);
    }
  };

  const performDragReschedule = async (entry, fromDate, toDate) => {
    if (!entry || entry.status === 'Locked') return;
    const D = String(fromDate || '').slice(0, 10);
    const N = String(toDate || '').slice(0, 10);
    if (!D || !N || D === N) return;
    if (ymdIsSunday(N)) return;

    setSaveLoading(true);
    setError('');
    try {
      await api({
        method: 'post',
        url: `/schedule/${entry.id}/move-day`,
        data: { fromDate: D, toDate: N, acknowledgeConflicts: false }
      });
      await loadEntries({ silent: true });
    } catch (e) {
      if (e.response?.status === 409 && e.response?.data?.code === 'SCHEDULE_CONFLICT') {
        setConflictDialog({
          message: e.response.data.message,
          conflicts: e.response.data.conflicts || [],
          code: e.response.data.code,
          moveDayPayload: { entryId: entry.id, fromDate: D, toDate: N }
        });
      } else {
        setError(e.response?.data?.message || 'Could not move this day');
      }
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDelete = async (entry) => {
    if (!window.confirm('Remove this schedule entry?')) return;
    try {
      await api({ method: 'delete', url: `/schedule/${entry.id}` });
      setModalOpen(false);
      loadEntries();
    } catch (e) {
      setError(e.response?.data?.message || 'Delete failed');
    }
  };

  const handleLock = async (entry) => {
    setLockLoading(true);
    try {
      await api({ method: 'post', url: `/schedule/${entry.id}/lock` });
      setModalOpen(false);
      loadEntries();
    } catch (e) {
      setError(e.response?.data?.message || 'Lock failed');
    } finally {
      setLockLoading(false);
    }
  };

  const handleUnlock = async (entry) => {
    setUnlockLoading(true);
    try {
      await api({ method: 'post', url: `/schedule/${entry.id}/unlock` });
      loadEntries();
      if (editing?.id === entry.id) {
        const refreshed = await api({ method: 'get', url: `/schedule/${entry.id}` });
        if (refreshed.data.success) setEditing(refreshed.data.data);
      }
    } catch (e) {
      setError(e.response?.data?.message || 'Unlock failed');
    } finally {
      setUnlockLoading(false);
    }
  };

  const openBreakdownModal = () => {
    if (!form.machineIds?.length) {
      setError('Select at least one machine on this entry.');
      return;
    }
    setError('');
    setBreakdownModalOpen(true);
  };

  const handlePrintSchedule = () => {
    setEmailSuccess('');
    window.print();
  };

  const openEmailScheduleModal = () => {
    setEmailSuccess('');
    setError('');
    const profileEmail = (selectedFilterCustomer?.email || '').trim();
    setEmailToList(profileEmail && EMAIL_ADDR_RE.test(profileEmail) ? [profileEmail] : []);
    setEmailCcList([]);
    setEmailBccList([]);
    setEmailShowCc(false);
    setEmailShowBcc(false);
    setEmailNote('');
    setEmailModalOpen(true);
  };

  const sendScheduleEmail = async () => {
    if (!filterCustomerId || !emailRecipientLooksValid) return;
    setEmailSending(true);
    setError('');
    try {
      const payload = {
        customerId: parseInt(filterCustomerId, 10),
        from: rangeFrom,
        to: rangeTo,
        calendarTitle: format(cursor, 'MMMM yyyy'),
        recipientEmails: emailToList,
        message: emailNote.trim() || undefined
      };
      if (emailCcList.length) payload.cc = emailCcList;
      if (emailBccList.length) payload.bcc = emailBccList;
      if (filterJobId) payload.jobId = parseInt(filterJobId, 10);
      const res = await api({ method: 'post', url: '/schedule/email-customer', data: payload });
      if (res.data?.success) {
        const sent = res.data.sentTo || emailToList;
        const sentLabel = Array.isArray(sent) ? sent.join(', ') : String(sent);
        const extras = [];
        if (res.data.sentCc?.length) extras.push(`Cc: ${res.data.sentCc.join(', ')}`);
        if (res.data.sentBcc?.length) extras.push(`Bcc: ${res.data.sentBcc.length} recipient(s)`);
        setEmailSuccess(
          `Email sent to ${sentLabel}${extras.length ? ` (${extras.join('; ')})` : ''}.`
        );
        setEmailModalOpen(false);
      } else {
        setError(res.data?.message || 'Email failed');
      }
    } catch (e) {
      setError(e.response?.data?.message || 'Email failed');
    } finally {
      setEmailSending(false);
    }
  };

  const handleBreakdownForMachine = async (entry, machineId) => {
    if (!entry || !machineId) return;
    const extend = window.prompt('Extend end date by how many working days? (0 to skip)', '0');
    if (extend === null) return;
    setBreakdownLoadingId(machineId);
    setError('');
    try {
      await api({
        method: 'post',
        url: `/schedule/${entry.id}/machines/${machineId}/breakdown`,
        data: { reason: 'Breakdown', extendEndByWorkingDays: parseInt(extend, 10) || 0 }
      });
      setBreakdownModalOpen(false);
      setModalOpen(false);
      loadEntries();
    } catch (e) {
      setError(e.response?.data?.message || 'Breakdown report failed');
    } finally {
      setBreakdownLoadingId(null);
    }
  };

  if (userLoading) {
    return <div style={{ padding: '2rem' }}>Loading…</div>;
  }
  if (!canView) {
    return (
      <div style={{ padding: '2rem' }}>
        <p>Scheduling is not available for your role.</p>
      </div>
    );
  }

  return (
    <div className="schedule-print-root" style={{ maxWidth: '1400px', margin: '0 auto' }}>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4 print-hidden">
        <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: sharedStyles.colors.text.primary, margin: 0 }}>
          Schedule
        </h1>
        <div className="d-flex flex-wrap align-items-center gap-2">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm print-hidden"
            onClick={() => setCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1))}
          >
            ← Prev
          </button>
          <span style={{ fontWeight: 600, minWidth: '160px', textAlign: 'center' }}>{format(cursor, 'MMMM yyyy')}</span>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm print-hidden"
            onClick={() => setCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1))}
          >
            Next →
          </button>
          {isAdmin && (
            <button type="button" className="btn btn-primary btn-sm print-hidden" onClick={openNew}>
              New entry
            </button>
          )}
          <button
            type="button"
            className="btn btn-outline-primary btn-sm print-hidden"
            onClick={handlePrintSchedule}
            title="Opens print preview in landscape (while you stay on this page). Enable Background graphics so entry colors print."
          >
            <i className="bi bi-printer me-1" aria-hidden />
            Print
          </button>
          {isAdmin && (
            <button
              type="button"
              className="btn btn-outline-primary btn-sm print-hidden"
              onClick={openEmailScheduleModal}
              disabled={!filterCustomerId}
              title={
                filterCustomerId
                  ? "Email this month's visible date range to the customer (respects Job filter if set)."
                  : 'Choose a customer in the filters below first.'
              }
            >
              <i className="bi bi-envelope me-1" aria-hidden />
              Email customer
            </button>
          )}
        </div>
      </div>

      <div className="schedule-print-email-branded">
        <div className="schedule-print-email-card">
          <div className="d-none d-print-block schedule-print-email-header-bar">
            <table cellPadding="0" cellSpacing="0" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={{ verticalAlign: 'middle' }}>
                    <h1
                      style={{
                        margin: 0,
                        color: '#ffffff',
                        fontSize: '24px',
                        fontWeight: 'bold',
                        fontFamily: 'Arial, sans-serif'
                      }}
                    >
                      Griffin Landworks
                    </h1>
                    <p
                      style={{
                        margin: '10px 0 0 0',
                        color: '#ffffff',
                        fontSize: '16px',
                        fontFamily: 'Arial, sans-serif'
                      }}
                    >
                      Equipment schedule
                    </p>
                  </td>
                  <td
                    style={{
                      verticalAlign: 'middle',
                      textAlign: 'right',
                      width: '150px'
                    }}
                  >
                    <img
                      src={`${window.location.origin}/${encodeURIComponent('thumbnail_GLW Logo (new).png')}`}
                      alt="Griffin Landworks Logo"
                      style={{
                        maxWidth: '150px',
                        maxHeight: '80px',
                        height: 'auto',
                        display: 'block',
                        marginLeft: 'auto'
                      }}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="schedule-print-email-content-wrap">
            <div className="d-none d-print-block schedule-print-email-body-intro">
              <h2
                style={{
                  margin: '0 0 20px 0',
                  color: '#1a1f36',
                  fontSize: '20px',
                  borderBottom: '2px solid #e5e9f0',
                  paddingBottom: '10px',
                  fontFamily: 'Arial, sans-serif',
                  fontWeight: 'bold'
                }}
              >
                Equipment schedule
              </h2>
              <p
                style={{
                  margin: '0 0 16px 0',
                  color: '#697386',
                  lineHeight: 1.6,
                  fontSize: '14px',
                  fontFamily: 'Arial, sans-serif'
                }}
              >
                <strong style={{ color: '#1a1f36' }}>{format(cursor, 'MMMM yyyy')}</strong>
                {' · '}
                {rangeFrom} to {rangeTo}
                {selectedFilterCustomer ? (
                  <>
                    <br />
                    <span style={{ color: '#697386' }}>Customer: </span>
                    <span style={{ color: '#1a1f36' }}>{selectedFilterCustomer.companyName}</span>
                    {printHeaderJobTitle ? (
                      <>
                        <br />
                        <span style={{ color: '#697386' }}>Job: </span>
                        <span style={{ color: '#1a1f36' }}>{printHeaderJobTitle}</span>
                      </>
                    ) : null}
                  </>
                ) : null}
              </p>
            </div>

            {error && <div className="alert alert-danger py-2">{error}</div>}
            {emailSuccess && <div className="alert alert-success py-2 print-hidden">{emailSuccess}</div>}

      <div className="row g-2 mb-2 print-hidden">
        <div className="col-12 col-sm-10 col-md-8 col-lg-6">
          <label className="form-label small mb-0" htmlFor="schedule-filter-search">
            Search
          </label>
          <input
            id="schedule-filter-search"
            type="search"
            className="form-control form-control-sm"
            placeholder="Task, customer, job, ticket, machine, notes"
            value={filterSearch}
            onChange={(e) => setFilterSearch(e.target.value)}
            autoComplete="off"
          />
        </div>
      </div>
      <div className="row g-2 mb-3 print-hidden">
        <div className="col-6 col-md-3">
          <label className="form-label small mb-0">Customer</label>
          <select
            className="form-select form-select-sm"
            value={filterCustomerId}
            onChange={(e) => setFilterCustomerId(e.target.value)}
          >
            <option value="">All</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.companyName}
              </option>
            ))}
          </select>
        </div>
        <div className="col-6 col-md-3">
          <label className="form-label small mb-0">Job</label>
          <select
            className="form-select form-select-sm"
            value={filterJobId}
            onChange={(e) => setFilterJobId(e.target.value)}
            disabled={!filterCustomerId}
          >
            <option value="">All</option>
            {jobsForFilter.map((j) => (
                <option key={j.id} value={j.id}>
                  {j.title}
                </option>
              ))}
          </select>
        </div>
        <div className="col-6 col-md-3">
          <label className="form-label small mb-0">Machine</label>
          <select
            className="form-select form-select-sm"
            value={filterMachineId}
            onChange={(e) => setFilterMachineId(e.target.value)}
          >
            <option value="">All</option>
            {machines.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
                {machineSchedulingOptionSuffix(m)}
              </option>
            ))}
          </select>
        </div>
        {isAdmin && (
          <div className="col-6 col-md-3">
            <label className="form-label small mb-0">Assigned user</label>
            <select
              className="form-select form-select-sm"
              value={filterForemanId}
              onChange={(e) => setFilterForemanId(e.target.value)}
            >
              <option value="">All</option>
              {assignableUsersForSelect.map((u) => (
                  <option key={u.id} value={u.id}>
                    {assignableUserLabel(u)} ({u.role})
                  </option>
                ))}
            </select>
          </div>
        )}
      </div>

      {filterSearch.trim() && entries.length > 0 && visibleEntries.length === 0 && (
        <p className="small text-muted mb-2 mb-md-3 print-hidden">No schedule entries match your search.</p>
      )}

      <div
        className="schedule-calendar-frame border rounded"
        style={{ background: '#fff', minHeight: '420px' }}
      >
        <div
          className="schedule-calendar-scroll"
          style={{
            overflowX: 'auto',
            overflowY: 'visible',
            WebkitOverflowScrolling: 'touch',
            overscrollBehaviorX: 'contain'
          }}
        >
          <div className="schedule-calendar-inner" style={{ minWidth: '720px', width: '100%' }}>
            <div
              className="schedule-weekday-header"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                background: '#f1f5f9',
                borderBottom: '1px solid #e2e8f0',
                fontSize: '0.75rem',
                fontWeight: 600,
                color: '#64748b'
              }}
            >
              {weekdayLabels.map((w) => (
                <div key={w} className="py-2 text-center">
                  {w}
                </div>
              ))}
            </div>
            {loading ? (
              <div className="p-5 text-center text-muted">Loading calendar…</div>
            ) : (
              <div
                className="schedule-calendar-grid"
                style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}
              >
            {days.map((day) => {
              const sunday = day.getDay() === 0;
              const inMonth = isSameMonth(day, cursor);
              const dayEntries = sunday
                ? []
                : visibleEntries.filter((en) => entryCoversDay(en, new Date(day)));
              const cellKey = format(day, 'yyyy-MM-dd');
              const isDropTarget =
                isAdmin && !sunday && dragOverDayKey === cellKey && draggingEntryId != null;
              return (
                <div
                  key={day.toISOString()}
                  className="schedule-day-cell border-bottom border-end"
                  style={{
                    minWidth: 0,
                    minHeight: '100px',
                    padding: '4px',
                    background: sunday ? '#f8fafc' : inMonth ? '#fff' : '#fafbfc',
                    opacity: sunday ? 0.85 : 1,
                    outline: isDropTarget ? '2px dashed #2563eb' : 'none',
                    outlineOffset: '-2px',
                    transition: 'outline 0.12s ease'
                  }}
                  onDragOver={(e) => {
                    if (!isAdmin || sunday) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    setDragOverDayKey(cellKey);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const sourceCellAtDrop = dragSourceCellKey;
                    setDragOverDayKey(null);
                    setDraggingEntryId(null);
                    setDragSourceCellKey(null);
                    if (!isAdmin || sunday) return;
                    const raw = e.dataTransfer.getData(SCHEDULE_DRAG_MIME);
                    const parsed = parseScheduleDragPayload(raw, sourceCellAtDrop);
                    if (!parsed) return;
                    const dragged = entries.find((x) => x.id === parsed.id);
                    if (!dragged || dragged.status === 'Locked') return;
                    performDragReschedule(dragged, parsed.fromDate, cellKey);
                  }}
                >
                  <div
                    style={{
                      fontSize: '0.75rem',
                      fontWeight: inMonth ? 600 : 400,
                      color: inMonth ? '#0f172a' : '#94a3b8',
                      marginBottom: '4px'
                    }}
                  >
                    {format(day, 'd')}
                    {sunday && <span className="text-muted small"> · off</span>}
                  </div>
                  <div className="d-flex flex-column gap-1">
                    {dayEntries.map((en) => {
                      const canDrag = isAdmin && en.status !== 'Locked';
                      const unavailableParts = (en.Machines || [])
                        .filter((m) => isMachineSchedulingUnavailable(m, cellKey))
                        .map(
                          (m) =>
                            `${m.name}: ${machineSchedulingUnavailableNote(m, cellKey) || 'Unavailable'}`
                        );
                      const hasUnavailableMachine = unavailableParts.length > 0;
                      const dragHint =
                        'Click to edit. Drag from this day to move only this day to another date (multi-day tasks may split). Target must not fall on remaining days of this task.';
                      const titleParts = [en.taskDescription];
                      if (hasUnavailableMachine) {
                        titleParts.push(`Unavailable equipment: ${unavailableParts.join('; ')}`);
                      }
                      if (canDrag) titleParts.push(dragHint);
                      const chipTitle = titleParts.join(' — ');
                      return (
                        <div
                          key={`${en.id}-${cellKey}`}
                          role="button"
                          tabIndex={0}
                          draggable={canDrag}
                          className="schedule-entry-chip text-start p-1 rounded"
                          style={{
                            width: '100%',
                            minWidth: 0,
                            fontSize: '0.65rem',
                            lineHeight: 1.25,
                            background: en.color || '#5B8DEF',
                            color: '#fff',
                            border: 'none',
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            overflowWrap: 'anywhere',
                            overflow: 'visible',
                            cursor: canDrag ? 'grab' : 'pointer',
                            opacity:
                              draggingEntryId === en.id && dragSourceCellKey === cellKey ? 0.55 : 1,
                            ...(hasUnavailableMachine
                              ? { boxShadow: 'inset 0 0 0 2px rgba(253, 224, 71, 0.95)' }
                              : {})
                          }}
                          title={chipTitle}
                          onClick={() => openEdit(en)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              openEdit(en);
                            }
                          }}
                          onDragStart={(e) => {
                            if (!canDrag) {
                              e.preventDefault();
                              return;
                            }
                            e.dataTransfer.setData(
                              SCHEDULE_DRAG_MIME,
                              JSON.stringify({ id: en.id, fromDate: cellKey })
                            );
                            e.dataTransfer.effectAllowed = 'move';
                            setDraggingEntryId(en.id);
                            setDragSourceCellKey(cellKey);
                          }}
                          onDragEnd={() => {
                            setDraggingEntryId(null);
                            setDragSourceCellKey(null);
                            setDragOverDayKey(null);
                          }}
                        >
                          {en.status === 'Locked' ? '🔒 ' : ''}
                          {en.taskDescription}
                          {hasUnavailableMachine ? ' ⚠' : ''}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
              </div>
            )}
          </div>
        </div>
      </div>

            <div className="d-none d-print-block schedule-print-entries-table">
              <h3
                style={{
                  margin: '24px 0 16px 0',
                  color: '#1a1f36',
                  fontSize: '18px',
                  borderBottom: '2px solid #e5e9f0',
                  paddingBottom: '10px',
                  fontFamily: 'Arial, sans-serif',
                  fontWeight: 'bold'
                }}
              >
                Scheduled entries
              </h3>
              <table
                cellPadding="0"
                cellSpacing="0"
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '11px',
                  fontFamily: 'Arial, sans-serif',
                  color: '#1a1f36'
                }}
              >
                <thead>
                  <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e5e9f0' }}>
                    {['Start', 'End', 'Task', 'Job', 'Ticket', 'Machines', 'Status'].map((label) => (
                      <th
                        key={label}
                        style={{
                          padding: '10px 8px',
                          textAlign: 'left',
                          color: '#1a1f36',
                          fontWeight: 'bold',
                          borderBottom: '2px solid #e5e9f0'
                        }}
                      >
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {printTableEntries.length === 0 ? (
                    <tr>
                      <td
                        colSpan={7}
                        style={{
                          padding: '16px',
                          textAlign: 'center',
                          color: '#697386',
                          border: '1px solid #e5e9f0'
                        }}
                      >
                        No schedule entries in this view.
                      </td>
                    </tr>
                  ) : (
                    printTableEntries.map((en) => {
                      const accent = entryAccentColorForPrint(en);
                      const machines =
                        (en.Machines || [])
                          .map((m) => m.name)
                          .filter(Boolean)
                          .join(', ') || '—';
                      const ticket =
                        en.Ticket != null && en.Ticket.ticketNumber != null && en.Ticket.ticketNumber !== ''
                          ? String(en.Ticket.ticketNumber)
                          : '—';
                      const td = (content, extra = {}) => (
                        <td
                          style={{
                            padding: '10px 8px',
                            border: '1px solid #e5e9f0',
                            color: '#1a1f36',
                            verticalAlign: 'top',
                            ...extra
                          }}
                        >
                          {content}
                        </td>
                      );
                      return (
                        <tr key={en.id}>
                          {td(en.startDate || '—', {
                            borderLeft: `4px solid ${accent}`
                          })}
                          {td(en.endDate || '—')}
                          {td(en.taskDescription || '—')}
                          {td(en.Job?.title || '—')}
                          {td(ticket)}
                          {td(machines)}
                          {td(en.status || '—')}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="d-none d-print-block schedule-print-email-footer">
              <p
                style={{
                  margin: '0 0 10px 0',
                  color: '#697386',
                  fontSize: '14px',
                  textAlign: 'center',
                  fontFamily: 'Arial, sans-serif'
                }}
              >
                <strong>Griffin Landworks</strong>
              </p>
              <p
                style={{
                  margin: 0,
                  color: '#697386',
                  fontSize: '12px',
                  textAlign: 'center',
                  lineHeight: 1.6,
                  fontFamily: 'Arial, sans-serif'
                }}
              >
                If you have questions about dates or equipment, contact Griffin Landworks.
              </p>
            </div>
          </div>
        </div>
      </div>

      <p className="small text-muted mt-2 mb-0 print-hidden">
        On narrow screens, scroll sideways inside the calendar to see every weekday (Sat included). Sundays are
        non-working days. Working-day duration skips Sundays. Colors are per schedule entry. A yellow
        ring (and ⚠) on a chip means at least one machine on that entry is marked unavailable for scheduling on that
        calendar day.
        {isAdmin && (
          <>
            {' '}
            <strong>Drag &amp; drop:</strong> drag from a specific day to move only that day to another date (long
            tasks split into segments). The drop date cannot be a Sunday and cannot be a day still covered by the
            rest of that task. Locked rows cannot be dragged.
          </>
        )}
      </p>

      {emailModalOpen && (
        <>
          <div className="modal-backdrop fade show" style={{ zIndex: 1620 }} />
          <div className="modal fade show d-block" tabIndex="-1" style={{ zIndex: 1630 }}>
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Email schedule to customer</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setEmailModalOpen(false)}
                    aria-label="Close"
                  />
                </div>
                <div className="modal-body">
                  <p className="small text-muted mb-3">
                    Sends a branded email with a <strong>month calendar</strong> (same date range as the grid) and a{' '}
                    detailed table for <strong>{selectedFilterCustomer?.companyName || 'this customer'}</strong>,{' '}
                    <strong>{rangeFrom}</strong>–<strong>{rangeTo}</strong>.
                    {filterJobId
                      ? ' Only the job selected in filters is included.'
                      : ' All jobs for this customer in that range are included.'}
                  </p>

                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <span className="form-label mb-0">To</span>
                    <div className="btn-group btn-group-sm" role="group" aria-label="Show Cc or Bcc">
                      {!emailShowCc ? (
                        <button
                          type="button"
                          className="btn btn-link btn-sm text-decoration-none py-0"
                          onClick={() => setEmailShowCc(true)}
                        >
                          Cc
                        </button>
                      ) : null}
                      {!emailShowBcc ? (
                        <button
                          type="button"
                          className="btn btn-link btn-sm text-decoration-none py-0"
                          onClick={() => setEmailShowBcc(true)}
                        >
                          Bcc
                        </button>
                      ) : null}
                    </div>
                  </div>
                  <EmailChipField
                    id="schedule-email-to"
                    emails={emailToList}
                    onChange={setEmailToList}
                    excludedEmails={emailToExcluded}
                    placeholder={selectedFilterCustomer?.email || 'name@example.com'}
                    hint="Type an email and press Enter or comma. You can add several people."
                  />

                  {emailShowCc ? (
                    <EmailChipField
                      id="schedule-email-cc"
                      label="Cc"
                      emails={emailCcList}
                      onChange={setEmailCcList}
                      excludedEmails={emailCcExcluded}
                      placeholder="colleague@example.com"
                      hint="Copied on the email (visible to everyone)."
                    />
                  ) : null}

                  {emailShowBcc ? (
                    <EmailChipField
                      id="schedule-email-bcc"
                      label="Bcc"
                      emails={emailBccList}
                      onChange={setEmailBccList}
                      excludedEmails={emailBccExcluded}
                      placeholder="internal@example.com"
                      hint="Blind copy — other recipients will not see these addresses."
                    />
                  ) : null}

                  <div className="mb-2">
                    <label className="form-label" htmlFor="schedule-email-note">
                      Optional note to the customer
                    </label>
                    <textarea
                      id="schedule-email-note"
                      className="form-control"
                      rows={3}
                      value={emailNote}
                      onChange={(e) => setEmailNote(e.target.value)}
                      placeholder="Short message shown at the top of the email…"
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setEmailModalOpen(false)}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={emailSending || !emailRecipientLooksValid}
                    onClick={sendScheduleEmail}
                  >
                    {emailSending
                      ? 'Sending…'
                      : emailToList.length > 1
                        ? `Send to ${emailToList.length} recipients`
                        : 'Send email'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {modalOpen && (
        <>
          <div className="modal-backdrop fade show" style={{ zIndex: 1600 }} />
          <div className="modal fade show d-block" tabIndex="-1" style={{ zIndex: 1610 }}>
            <div className="modal-dialog modal-lg modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{editing ? 'Schedule entry' : 'New schedule entry'}</h5>
                  <button type="button" className="btn-close" onClick={() => setModalOpen(false)} />
                </div>
                <div className="modal-body">
                  <div className="row g-2">
                    <div className="col-md-6">
                      <label className="form-label">Customer *</label>
                      <select
                        className="form-select"
                        disabled={!isAdmin || (editing && editing.status === 'Locked')}
                        value={form.customerId}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, customerId: e.target.value, jobId: '', ticketId: '' }))
                        }
                      >
                        <option value="">Select…</option>
                        {customers.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.companyName}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Job *</label>
                      <select
                        className="form-select"
                        disabled={!isAdmin || (editing && editing.status === 'Locked') || !form.customerId}
                        value={form.jobId}
                        onChange={(e) => setForm((f) => ({ ...f, jobId: e.target.value, ticketId: '' }))}
                      >
                        <option value="">Select…</option>
                        {jobsForCustomer.map((j) => (
                          <option key={j.id} value={j.id}>
                            {j.title}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Ticket (optional)</label>
                      <select
                        className="form-select"
                        disabled={
                          !isAdmin ||
                          (editing && editing.status === 'Locked') ||
                          !form.customerId ||
                          !form.jobId ||
                          jobTicketsLoading
                        }
                        value={form.ticketId}
                        onChange={(e) => setForm((f) => ({ ...f, ticketId: e.target.value }))}
                      >
                        <option value="">None</option>
                        {ticketsForScheduleSelect.map((t) => {
                          const wd = t.workDate ? String(t.workDate).slice(0, 10) : '';
                          const desc = (t.description || '').trim();
                          const descShort = desc.length > 40 ? `${desc.slice(0, 37)}...` : desc;
                          const labelParts = [`#${t.ticketNumber}`, wd, descShort].filter(Boolean);
                          return (
                            <option key={t.id} value={t.id}>
                              {labelParts.join(' · ')}
                            </option>
                          );
                        })}
                      </select>
                      {!form.customerId || !form.jobId ? (
                        <div className="form-text">Select customer and job to list tickets for this job.</div>
                      ) : jobTicketsLoading ? (
                        <div className="form-text">Loading tickets…</div>
                      ) : ticketsForScheduleSelect.length === 0 ? (
                        <div className="form-text">No tickets found for this job.</div>
                      ) : (
                        <div className="form-text">Link this schedule entry to an existing ticket, or leave as None.</div>
                      )}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Task / description *</label>
                      <input
                        type="text"
                        className="form-control"
                        disabled={!isAdmin || (editing && editing.status === 'Locked')}
                        value={form.taskDescription}
                        onChange={(e) => setForm((f) => ({ ...f, taskDescription: e.target.value }))}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Machines *</label>
                      <div
                        className="mb-2 p-2 border rounded"
                        style={{ minHeight: '2.5rem', backgroundColor: '#f8fafc' }}
                      >
                        {selectedMachinesOrdered.length === 0 ? (
                          <span className="text-muted small">No machines selected — open the picker below.</span>
                        ) : (
                          <div className="d-flex flex-wrap gap-2 align-items-center">
                            {selectedMachinesOrdered.map((m) => (
                              <span
                                key={m.id}
                                className="badge rounded-pill d-inline-flex align-items-center gap-1 py-2 px-3"
                                style={{
                                  fontSize: '0.8125rem',
                                  fontWeight: 500,
                                  backgroundColor: sharedStyles.colors?.primary?.main || '#2563eb',
                                  color: '#fff',
                                  ...(m.schedulingHint
                                    ? { boxShadow: 'inset 0 0 0 2px rgba(253, 224, 71, 0.95)' }
                                    : {})
                                }}
                                title={m.schedulingHint ? `${m.name} — ${m.schedulingHint}` : m.name}
                              >
                                <span className="text-truncate" style={{ maxWidth: '220px' }}>
                                  {m.schedulingHint ? '⚠ ' : ''}
                                  {m.name}
                                </span>
                                {!machinesFormDisabled && (
                                  <button
                                    type="button"
                                    className="btn btn-sm border-0 p-0 ms-1"
                                    style={{
                                      lineHeight: 1,
                                      color: '#fff',
                                      opacity: 0.9,
                                      background: 'transparent'
                                    }}
                                    aria-label={`Remove ${m.name}`}
                                    onClick={() => removeMachineId(m.id)}
                                  >
                                    <i className="bi bi-x-lg" style={{ fontSize: '0.7rem' }} />
                                  </button>
                                )}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="position-relative" ref={machinePickerRef}>
                        <button
                          type="button"
                          className="form-select text-start d-flex align-items-center justify-content-between"
                          style={{ cursor: machinesFormDisabled ? 'not-allowed' : 'pointer' }}
                          disabled={machinesFormDisabled}
                          aria-expanded={machinePickerOpen}
                          aria-haspopup="listbox"
                          onClick={() => !machinesFormDisabled && setMachinePickerOpen((o) => !o)}
                        >
                          <span className="text-truncate me-2">
                            {form.machineIds.length === 0
                              ? 'Click to choose machines…'
                              : `${form.machineIds.length} selected — click to add or change`}
                          </span>
                          <i
                            className={`bi bi-chevron-${machinePickerOpen ? 'up' : 'down'} flex-shrink-0`}
                            style={{ fontSize: '0.85rem', opacity: 0.65 }}
                          />
                        </button>

                        {machinePickerOpen && !machinesFormDisabled && (
                          <div
                            className="position-absolute top-100 start-0 end-0 mt-1 border rounded shadow bg-white"
                            style={{ zIndex: 20, maxHeight: 'min(320px, 55vh)' }}
                          >
                            <div className="p-2 border-bottom bg-light">
                              <input
                                type="search"
                                className="form-control form-control-sm"
                                placeholder="Search by name or model…"
                                value={machineSearch}
                                onChange={(e) => setMachineSearch(e.target.value)}
                                autoComplete="off"
                                autoFocus
                              />
                            </div>
                            <div style={{ maxHeight: '240px', overflowY: 'auto' }}>
                              {filteredMachinePickerList.length === 0 ? (
                                <div className="p-3 text-muted small text-center">
                                  No machines match your search.
                                </div>
                              ) : (
                                filteredMachinePickerList.map((m) => {
                                  const checked = form.machineIds.includes(m.id);
                                  return (
                                    <label
                                      key={m.id}
                                      className={`d-flex align-items-start gap-2 px-3 py-2 mb-0 border-bottom ${
                                        checked ? 'bg-primary bg-opacity-10' : ''
                                      }`}
                                      style={{ cursor: 'pointer' }}
                                    >
                                      <input
                                        type="checkbox"
                                        className="form-check-input mt-1 flex-shrink-0"
                                        checked={checked}
                                        onChange={() => toggleMachineId(m.id)}
                                      />
                                      <span className="flex-grow-1 small">
                                        <span className="fw-medium d-block">{m.name}</span>
                                        {m.model ? (
                                          <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                                            {m.model}
                                          </span>
                                        ) : null}
                                        {m.schedulingHint ? (
                                          <span
                                            className="d-block text-warning mt-1"
                                            style={{ fontSize: '0.75rem' }}
                                          >
                                            ⚠ {m.schedulingHint}
                                          </span>
                                        ) : null}
                                      </span>
                                    </label>
                                  );
                                })
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="form-text mt-1">
                        {form.machineIds.length} machine(s) on this entry. Open the picker to search and check
                        machines; click outside the panel to close it.
                      </div>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Start date *</label>
                      <input
                        type="date"
                        className="form-control"
                        disabled={!isAdmin || (editing && editing.status === 'Locked')}
                        value={form.startDate}
                        onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label d-flex align-items-center gap-2">
                        <input
                          type="checkbox"
                          checked={form.useWorkingDays}
                          disabled={!isAdmin || (editing && editing.status === 'Locked')}
                          onChange={(e) => setForm((f) => ({ ...f, useWorkingDays: e.target.checked }))}
                        />
                        Use working days
                      </label>
                      <input
                        type="number"
                        min={1}
                        className="form-control"
                        disabled={!isAdmin || !form.useWorkingDays || (editing && editing.status === 'Locked')}
                        value={form.workingDays}
                        onChange={(e) => setForm((f) => ({ ...f, workingDays: e.target.value }))}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Buffer (calendar days)</label>
                      <input
                        type="number"
                        min={0}
                        className="form-control"
                        disabled={!isAdmin || !form.useWorkingDays || (editing && editing.status === 'Locked')}
                        value={form.bufferDays}
                        onChange={(e) => setForm((f) => ({ ...f, bufferDays: e.target.value }))}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">End date {form.useWorkingDays ? '(auto)' : '*'}</label>
                      <input
                        type="date"
                        className="form-control"
                        disabled={
                          !isAdmin || form.useWorkingDays || (editing && editing.status === 'Locked')
                        }
                        value={form.endDate}
                        onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Assigned user (optional)</label>
                      <select
                        className="form-select"
                        disabled={!isAdmin || (editing && editing.status === 'Locked')}
                        value={form.assignedUserId}
                        onChange={(e) => setForm((f) => ({ ...f, assignedUserId: e.target.value }))}
                      >
                        <option value="">—</option>
                        {assignableUsersForSelect.map((u) => (
                            <option key={u.id} value={u.id}>
                              {assignableUserLabel(u)} ({u.role})
                            </option>
                          ))}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Status</label>
                      <select
                        className="form-select"
                        disabled={!isAdmin || (editing && editing.status === 'Locked')}
                        value={form.status}
                        onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                      >
                        <option value="Draft">Draft</option>
                        <option value="Confirmed">Confirmed</option>
                        <option value="Completed">Completed</option>
                      </select>
                    </div>
                    <div className="col-12">
                      <label className="form-label">Calendar color</label>
                      <div
                        className="p-3 border rounded"
                        style={{ backgroundColor: '#f8fafc' }}
                      >
                        <div className="d-flex flex-wrap gap-2 mb-3">
                          {SCHEDULE_COLOR_PRESETS.map((hex) => {
                            const selected =
                              !!form.color && form.color.toLowerCase() === hex.toLowerCase();
                            return (
                              <button
                                key={hex}
                                type="button"
                                disabled={!isAdmin || (editing && editing.status === 'Locked')}
                                title={hex}
                                aria-label={`Use color ${hex}`}
                                className="rounded border-0 p-0"
                                style={{
                                  width: 36,
                                  height: 36,
                                  backgroundColor: hex,
                                  cursor:
                                    !isAdmin || (editing && editing.status === 'Locked')
                                      ? 'not-allowed'
                                      : 'pointer',
                                  boxShadow: selected ? '0 0 0 3px #fff, 0 0 0 5px #1e293b' : 'none',
                                  opacity:
                                    !isAdmin || (editing && editing.status === 'Locked') ? 0.5 : 1
                                }}
                                onClick={() =>
                                  setForm((f) => ({
                                    ...f,
                                    color: hex
                                  }))
                                }
                              />
                            );
                          })}
                        </div>
                        <div className="d-flex flex-wrap align-items-center gap-3">
                          <div className="d-flex align-items-center gap-2">
                            <span className="small text-muted text-nowrap">Custom</span>
                            <input
                              type="color"
                              className="form-control form-control-color"
                              title="Pick a custom color"
                              disabled={!isAdmin || (editing && editing.status === 'Locked')}
                              value={isHexColor6(form.color) ? form.color : '#5B8DEF'}
                              onChange={(e) =>
                                setForm((f) => ({ ...f, color: e.target.value.toUpperCase() }))
                              }
                            />
                          </div>
                          {!machinesFormDisabled && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => setForm((f) => ({ ...f, color: '' }))}
                            >
                              Auto (server default)
                            </button>
                          )}
                          <span className="small font-monospace text-muted">
                            {isHexColor6(form.color)
                              ? form.color
                              : 'Auto — saved without a custom color'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="col-12">
                      <label className="form-label d-flex align-items-center gap-2">
                        <input
                          type="checkbox"
                          checked={form.notificationsEnabled}
                          disabled={!isAdmin || (editing && editing.status === 'Locked')}
                          onChange={(e) => setForm((f) => ({ ...f, notificationsEnabled: e.target.checked }))}
                        />
                        Email reminders (2 days before + morning of start) to assigned user
                      </label>
                    </div>
                    <div className="col-12">
                      <label className="form-label">Notes / special instructions</label>
                      <textarea
                        className="form-control"
                        rows={2}
                        disabled={!isAdmin || (editing && editing.status === 'Locked')}
                        value={form.notes}
                        onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                      />
                    </div>
                  </div>
                </div>
                <div className="modal-footer flex-wrap gap-2">
                  {isAdmin && editing && editing.status === 'Confirmed' && (
                    <button
                      type="button"
                      className="btn btn-outline-dark d-inline-flex align-items-center gap-2"
                      disabled={scheduleModalBusy}
                      onClick={() => handleLock(editing)}
                    >
                      {lockLoading && (
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                      )}
                      {lockLoading ? 'Locking…' : 'Lock'}
                    </button>
                  )}
                  {isAdmin && editing && editing.status === 'Locked' && (
                    <button
                      type="button"
                      className="btn btn-outline-warning d-inline-flex align-items-center gap-2"
                      disabled={scheduleModalBusy}
                      onClick={() => handleUnlock(editing)}
                    >
                      {unlockLoading && (
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                      )}
                      {unlockLoading ? 'Unlocking…' : 'Unlock'}
                    </button>
                  )}
                  {isAdmin && editing && editing.status !== 'Locked' && (
                    <button
                      type="button"
                      className="btn btn-outline-danger"
                      disabled={scheduleModalBusy}
                      onClick={() => handleDelete(editing)}
                    >
                      Delete
                    </button>
                  )}
                  {isAdmin && editing && editing.status !== 'Locked' && (
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      disabled={scheduleModalBusy}
                      onClick={openBreakdownModal}
                    >
                      Report equipment breakdown
                    </button>
                  )}
                  <button type="button" className="btn btn-secondary" onClick={() => setModalOpen(false)}>
                    Close
                  </button>
                  {isAdmin && (!editing || editing.status !== 'Locked') && (
                    <button
                      type="button"
                      className="btn btn-primary d-inline-flex align-items-center gap-2"
                      disabled={scheduleModalBusy}
                      onClick={() => saveEntry(false)}
                    >
                      {saveLoading && (
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                      )}
                      {saveLoading ? 'Saving…' : 'Save'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {breakdownModalOpen && editing && (
        <>
          <div
            className="modal-backdrop fade show"
            style={{ zIndex: 1680 }}
            onClick={() => breakdownLoadingId == null && setBreakdownModalOpen(false)}
          />
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            aria-labelledby="breakdown-modal-title"
            style={{ zIndex: 1690 }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title" id="breakdown-modal-title">
                    Report equipment breakdown
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    disabled={breakdownLoadingId != null}
                    onClick={() => setBreakdownModalOpen(false)}
                    aria-label="Close"
                  />
                </div>
                <div className="modal-body">
                  <p className="small text-muted mb-3">
                    Pick the machine that broke down. It will be marked unavailable for scheduling, and you can extend
                    this entry&apos;s end date in the next prompt. When the machine is fixed, go to{' '}
                    <strong>Machines</strong>, open that unit, and choose <strong>Mark available for scheduling</strong>.
                  </p>
                  {selectedMachinesOrdered.length === 0 ? (
                    <p className="text-muted small mb-0">No machines on this entry. Add machines or close and edit.</p>
                  ) : (
                    <ul className="list-group list-group-flush">
                      {selectedMachinesOrdered.map((m) => (
                        <li
                          key={m.id}
                          className="list-group-item d-flex justify-content-between align-items-center flex-wrap gap-2 px-0"
                        >
                          <div className="min-w-0">
                            <div className="fw-medium text-truncate">{m.name}</div>
                            {m.model ? (
                              <div className="small text-muted text-truncate">{m.model}</div>
                            ) : null}
                          </div>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary flex-shrink-0 d-inline-flex align-items-center gap-2"
                            disabled={breakdownLoadingId != null}
                            onClick={() => handleBreakdownForMachine(editing, m.id)}
                          >
                            {breakdownLoadingId === m.id && (
                              <span
                                className="spinner-border spinner-border-sm"
                                role="status"
                                aria-hidden="true"
                              />
                            )}
                            {breakdownLoadingId === m.id ? 'Reporting…' : 'Report breakdown'}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={breakdownLoadingId != null}
                    onClick={() => setBreakdownModalOpen(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {conflictDialog && (
        <>
          <div className="modal-backdrop fade show" style={{ zIndex: 1700 }} />
          <div className="modal fade show d-block" style={{ zIndex: 1710 }}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Schedule conflict</h5>
                  <button
                    type="button"
                    className="btn-close"
                    disabled={saveLoading}
                    onClick={() => setConflictDialog(null)}
                  />
                </div>
                <div className="modal-body">
                  <p>{conflictDialog.message}</p>
                  <ul className="small">
                    {(conflictDialog.conflicts || []).map((c) => (
                      <li key={c.scheduleEntryId}>
                        #{c.scheduleEntryId} {c.taskDescription} ({c.startDate} → {c.endDate})
                      </li>
                    ))}
                  </ul>
                  <p className="small text-muted mb-0">You can acknowledge and save anyway.</p>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={saveLoading}
                    onClick={() => setConflictDialog(null)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-warning d-inline-flex align-items-center gap-2"
                    disabled={saveLoading}
                    onClick={async () => {
                      if (conflictDialog?.moveDayPayload) {
                        setSaveLoading(true);
                        try {
                          const { entryId, fromDate, toDate } = conflictDialog.moveDayPayload;
                          await api({
                            method: 'post',
                            url: `/schedule/${entryId}/move-day`,
                            data: { fromDate, toDate, acknowledgeConflicts: true }
                          });
                          setConflictDialog(null);
                          await loadEntries({ silent: true });
                        } catch (err) {
                          setError(
                            err.response?.data?.message || 'Could not move after acknowledgement'
                          );
                        } finally {
                          setSaveLoading(false);
                        }
                      } else {
                        saveEntry(true);
                      }
                    }}
                  >
                    {saveLoading && (
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                    )}
                    {saveLoading ? 'Saving…' : 'Save with acknowledgement'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Schedule;
